import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { initialTabsState, makeHomeTab, tabsReducer, type TabsAction, type TabMeta } from './reducer';
import { HOME_PATHNAME, type LopTab, type LopTabLocation, type TabsState } from './types';

const STORAGE_KEY = 'lop:tabs';

interface StoredTabsState {
  tabs: LopTab[];
  activeKey: string;
  mru: string[];
}

function isValidTab(x: unknown): x is LopTab {
  if (typeof x !== 'object' || x === null) return false;
  const t = x as Record<string, unknown>;
  return (
    typeof t.key === 'string' &&
    typeof t.location === 'object' &&
    t.location !== null &&
    typeof (t.location as LopTabLocation).pathname === 'string' &&
    typeof t.title === 'string' &&
    typeof t.pinned === 'boolean'
  );
}

function loadPersisted(): TabsState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredTabsState>;
    if (!Array.isArray(parsed.tabs) || typeof parsed.activeKey !== 'string' || !Array.isArray(parsed.mru)) {
      return null;
    }
    const tabs = parsed.tabs.filter(isValidTab);
    if (tabs.length === 0) return null;
    if (!tabs.some((t) => t.pinned)) tabs.unshift(makeHomeTab());
    const activeKey = tabs.some((t) => t.key === parsed.activeKey) ? parsed.activeKey : tabs[0].key;
    const mru = (parsed.mru as string[]).filter((k) => tabs.some((t) => t.key === k));
    if (!mru.includes(activeKey)) mru.unshift(activeKey);
    return { tabs, activeKey, mru, capBlocked: null };
  } catch {
    return null;
  }
}

function persist(state: TabsState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // storage unavailable (private mode / quota) — tabs just won't survive a reload
  }
}

// Wipe the persisted tabs. Called from AuthContext.logout alongside
// clearRecentPages (banking privacy — the next user on a shared browser must
// not see the prior user's open tabs). Also resets any LIVE TabsProvider
// instance (closeAllTabs()) so a session that doesn't hard-navigate away
// immediately still reflects the clear. Note: if a TabsProvider IS live, its
// reactive persist effect re-writes storage right after CLOSE_ALL resolves —
// so the end state is "only the pinned home tab", not a literally-absent
// storage key. That's equivalent for privacy purposes (no trace of the prior
// user's actual screens survives) and is the normal, expected outcome.
export function clearTabs(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  closeAllTabs();
}

// Imperative escape hatch for code outside the tree that owns the tabs UI
// (today: AuthContext.logout via clearTabs(); reserved for a future tenant
// switcher per plan §2 D7 — "כל מחליף-טננט עתידי חייב לקרוא ל-closeAll").
// A module-level ref to the live dispatch, set by whichever TabsProvider is
// currently mounted. No-ops when nothing is mounted.
let liveDispatch: React.Dispatch<TabsAction> | null = null;

export function closeAllTabs(): void {
  liveDispatch?.({ type: 'CLOSE_ALL' });
}

export interface TabsContextValue {
  tabs: LopTab[];
  activeKey: string;
  mru: string[];
  /** Set when a navigation hit the MAX_TABS cap — the location it was
   *  trying to reach. Non-null while LopTabsCapModal is open. See reducer.ts. */
  capBlocked: LopTabLocation | null;
  // User-driven actions (LopTabBar, its context menu).
  activate: (key: string) => void;
  close: (key: string) => void;
  closeOthers: (key: string) => void;
  closeAll: () => void;
  setDirty: (key: string, dirty: boolean) => void;
  setTitle: (key: string, title: string) => void;
  // Router-driven actions (TabRouterSync only — not for screens).
  navOpenOrFocus: (location: LopTabLocation, meta: TabMeta) => void;
  navReplace: (location: LopTabLocation, meta: TabMeta) => void;
  /** TabRouterSync only — a navigation was refused at the MAX_TABS cap (D5).
   *  Stores `location` so LopTabsCapModal can show/perform it on אישור;
   *  never touches tabs/activeKey/mru. */
  reportCapBlocked: (location: LopTabLocation) => void;
  /** LopTabsCapModal only — ביטול/Escape/backdrop, or after אישור performs
   *  the pending navigation. Clears `capBlocked`. */
  dismissCapBlocked: () => void;
}

const TabsContext = createContext<TabsContextValue | undefined>(undefined);

export function TabsProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(tabsReducer, undefined, () => loadPersisted() ?? initialTabsState());

  useEffect(() => {
    liveDispatch = dispatch;
    return () => {
      if (liveDispatch === dispatch) liveDispatch = null;
    };
  }, []);

  // Skip persisting the very first render's state back to storage — it's
  // either exactly what we just loaded (no-op write) or the fresh-session
  // default, both harmless, but this avoids a redundant write on mount.
  const skipFirstPersist = useRef(true);
  useEffect(() => {
    if (skipFirstPersist.current) {
      skipFirstPersist.current = false;
      return;
    }
    persist(state);
  }, [state]);

  // Global unsaved-changes guard (plan §3.6, banking safety — there was none
  // before internal tabs existed). Any dirty tab, anywhere, blocks a hard
  // navigation away from the app (reload/close/URL bar). The prompt text
  // itself is not customizable by any modern browser — `returnValue` just has
  // to be set to trigger the native confirm.
  useEffect(() => {
    function onBeforeUnload(e: BeforeUnloadEvent) {
      if (state.tabs.some((t) => t.dirty)) {
        e.preventDefault();
        e.returnValue = '';
      }
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [state.tabs]);

  const activate = useCallback((key: string) => dispatch({ type: 'ACTIVATE', key }), []);
  const close = useCallback((key: string) => dispatch({ type: 'CLOSE', key }), []);
  const closeOthers = useCallback((key: string) => dispatch({ type: 'CLOSE_OTHERS', key }), []);
  const closeAll = useCallback(() => dispatch({ type: 'CLOSE_ALL' }), []);
  const setDirty = useCallback((key: string, dirty: boolean) => dispatch({ type: 'SET_DIRTY', key, dirty }), []);
  const setTitle = useCallback((key: string, title: string) => dispatch({ type: 'SET_TITLE', key, title }), []);
  const navOpenOrFocus = useCallback(
    (location: LopTabLocation, meta: TabMeta) => dispatch({ type: 'NAV_OPEN_OR_FOCUS', location, meta }),
    [],
  );
  const navReplace = useCallback(
    (location: LopTabLocation, meta: TabMeta) => dispatch({ type: 'NAV_REPLACE', location, meta }),
    [],
  );
  const reportCapBlocked = useCallback(
    (location: LopTabLocation) => dispatch({ type: 'CAP_BLOCKED', location }),
    [],
  );
  const dismissCapBlocked = useCallback(() => dispatch({ type: 'CAP_DISMISS' }), []);

  const value = useMemo<TabsContextValue>(
    () => ({
      tabs: state.tabs,
      activeKey: state.activeKey,
      mru: state.mru,
      capBlocked: state.capBlocked,
      activate,
      close,
      closeOthers,
      closeAll,
      setDirty,
      setTitle,
      navOpenOrFocus,
      navReplace,
      reportCapBlocked,
      dismissCapBlocked,
    }),
    [
      state,
      activate,
      close,
      closeOthers,
      closeAll,
      setDirty,
      setTitle,
      navOpenOrFocus,
      navReplace,
      reportCapBlocked,
      dismissCapBlocked,
    ],
  );

  return <TabsContext.Provider value={value}>{children}</TabsContext.Provider>;
}

export function useTabs(): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (ctx === undefined) {
    throw new Error('useTabs must be used within a TabsProvider');
  }
  return ctx;
}

export { HOME_PATHNAME };
